import { useEffect, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { RowLink } from '@/components/admin/DataTable';
import BroadcastDialog from '@/components/admin/BroadcastDialog';
import { AUDIENCES, BROADCAST_KINDS, BROADCAST_KIND_STYLES } from '@/shared/constants';
import { AUDIENCE_LABELS } from '@/shared/audience';
import { BROADCAST_FIELDS } from '@/shared/fields';
import { paged } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { eq, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/broadcasts', layout: 'owner', access: "profile === 'Admin_c'" };
export const nav = { icon: 'Megaphone', label: 'Broadcasts', section: 'Owner', profiles: ['Admin_c'], order: 85 };

const PAGE_SIZE = 20;

export default function AdminBroadcasts() {
  const [kind, setKind] = useState('all');
  const [audience, setAudience] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [kind, audience, q]);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('broadcasts_c').select(BROADCAST_FIELDS);
    if (kind !== 'all') query = query.where(eq('kind_c', kind));
    if (audience !== 'all') query = query.where(eq('audience_c', audience));
    if (q) query = query.where(contains('Name', q));
    return query.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [kind, audience, q, page]);

  const list = data?.rows ?? [];
  const filtered = kind !== 'all' || audience !== 'all' || !!q;

  function clear() {
    setSearch('');
    setKind('all');
    setAudience('all');
  }

  const columns = [
    {
      label: 'Subject',
      render: (b) => (
        <RowLink to={`/admin/broadcasts/${b.Id}`} sub={formatLocalDate(b.CreatedOn, DATE_FORMATS.WITH_TIME)}>
          {b.Name}
        </RowLink>
      ),
    },
    { label: 'Kind', render: (b) => <StatusBadge value={b.kind_c} styles={BROADCAST_KIND_STYLES} /> },
    {
      label: 'Audience',
      className: 'hidden md:table-cell text-muted-foreground',
      render: (b) => AUDIENCE_LABELS[b.audience_c] ?? b.audience_c,
    },
    { label: 'People', className: 'tabular-nums', render: (b) => Number(b.recipients_c || 0) },
    { label: 'Sent by', className: 'hidden md:table-cell text-muted-foreground', render: (b) => b.sent_by_c },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Owner"
        title="Broadcasts"
        description="Emails to a group of customers. Service notices go to everyone in the audience; promos only to people who opted in."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            New broadcast
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search subjects…" />
        <FilterSelect value={kind} onChange={setKind} values={BROADCAST_KINDS} allLabel="All kinds" />
        <FilterSelect value={audience} onChange={setAudience} values={AUDIENCES} labels={AUDIENCE_LABELS} allLabel="All audiences" className="sm:w-56" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Megaphone',
          title: filtered ? 'No broadcasts match' : 'Nothing sent yet',
          description: filtered ? 'Nothing fits these filters.' : 'Warn everyone before the next maintenance window.',
          action: filtered ? clear : () => setCreateOpen(true),
          actionLabel: filtered ? 'Clear filters' : 'New broadcast',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <BroadcastDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={run} />
    </div>
  );
}
